import { useRef } from 'react';
import { Form } from 'react-bootstrap';
import { format } from 'date-fns';
import { joinLabeledValue } from '../../utils/joinLabeledValue';

/** yyyy-MM-dd → yyyy/MM/dd（表示用） */
export function formatIsoDateForDisplay(isoDate: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return isoDate.replace(/-/g, '/');
  }
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) {
    return isoDate.replace(/-/g, '/').slice(0, 10);
  }
  return format(d, 'yyyy/MM/dd');
}

/** ツールバー用。`yyyy/MM/dd` + form-control-sm 余白が切れない幅 */
export const BASE_DATE_FIELD_COMPACT_WIDTH = '7.75rem';

type BaseDateFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (yyyyMmDd: string) => void;
  max?: string;
  lang?: string;
  title?: string;
  /** ラッパー幅（既定はコンパクト日付表示が切れない幅） */
  width?: string;
  className?: string;
  /** モバイル等でラベル行を省略（aria-label に label を残す） */
  hideLabel?: boolean;
};

/**
 * ロケール表示テキスト＋不可視 date でピッカーを開く基準日フィールド。
 * Reports / UsageRecords で同一操作感にする。
 */
export default function BaseDateField({
  id,
  label,
  value,
  onChange,
  max,
  lang,
  title,
  width = BASE_DATE_FIELD_COMPACT_WIDTH,
  className,
  hideLabel = false,
}: BaseDateFieldProps) {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const displayId = `${id}-display`;
  const displayValue = formatIsoDateForDisplay(value);

  const openPicker = () => {
    const el = hiddenInputRef.current;
    if (!el) return;
    try {
      el.showPicker?.();
    } catch {
      el.click();
    }
  };

  return (
    <div className={className} style={{ width, minWidth: width, flexShrink: 0 }}>
      {hideLabel ? null : (
        <Form.Label htmlFor={displayId} className="small text-muted mb-1">
          {label}
        </Form.Label>
      )}
      <div className="position-relative">
        <Form.Control
          id={displayId}
          type="text"
          readOnly
          size="sm"
          value={displayValue}
          title={title ?? label}
          aria-label={joinLabeledValue(label, displayValue)}
          style={{
            cursor: 'pointer',
            backgroundColor: '#fff',
            fontVariantNumeric: 'tabular-nums',
          }}
          onClick={openPicker}
        />
        <Form.Control
          ref={hiddenInputRef}
          id={id}
          type="date"
          value={value}
          max={max}
          lang={lang}
          onChange={(e) => {
            const selected = e.target.value;
            if (!selected) return;
            if (max && selected > max) {
              onChange(max);
              return;
            }
            onChange(selected);
          }}
          tabIndex={-1}
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            cursor: 'pointer',
            width: '100%',
            height: '100%',
          }}
        />
      </div>
    </div>
  );
}
